import Head from 'next/head'
import Link from 'next/link'
import { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import ContactForm from 'modules/ContactForm'

import ExtraLargeHeader from 'components/ExtraLargeHeader'
import ChatIcon from 'components/SVG/ConversationBubbleDouble'
import EmailIcon from 'components/SVG/Email'
import PhoneIcon from 'components/SVG/Phone'
import QuestionMarkIcon from 'components/SVG/QuestionMarkCircleFilled'
import Spacer from 'components/Spacer'

import { prepopulateIntercomMessage } from 'services/intercom'

const MaxWidthContainer = styled.div`
  max-width: 640px;
  padding: 30px ${props => props.theme.metrics.gutterPhone}px;
  margin: 0 auto;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
`

const IconContainer = styled.div`
  height: 36px;
  width: 36px;
  border-radius: 18px;
  border: 2px solid ${props => props.theme.colors.black};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`

const StyledText = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.colors.black};
`

const StyledAnchor = styled.a`
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.colors.black};
  text-decoration-color: ${props => props.theme.colors.black};
  text-decoration: underline;

  cursor: pointer;

  &:visited {
    color: ${props => props.theme.colors.black};
  }
`

const StyledPseudoAnchor = styled.span`
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.colors.black};
  text-decoration-color: ${props => props.theme.colors.black};
  text-decoration: underline;

  cursor: pointer;

  &:visited {
    color: ${props => props.theme.colors.black};
  }
`

const ContactUsPage = () => {
  const theme = useContext(ThemeContext)

  const openChat = () => prepopulateIntercomMessage('Hello')

  return (
    <MaxWidthContainer>
      <Head>
        <title>Contact Us: Email, Phone number, Chat, and FAQs | HomeLister</title>
        <meta
          name="description"
          content="Find contact information from HomeLister for help, questions and support. Sell your home today with HomeLister."
        />
        <meta
          name="keywords"
          content="Contact Us, HomeLister"
        />
        <meta
          name="og:description"
          property="og:description"
          content="Find contact information from HomeLister for help, questions and support. Sell your home today with HomeLister."
        />
      </Head>
      <Spacer size={70} />
      <ExtraLargeHeader
        title="Contact Us"
        subtitle="Here are a few ways to contact us."
        noMargin
      />
      <Spacer size={50} />
      <FlexRow>
        <IconContainer>
          <PhoneIcon
            width={18}
            height={18}
            fill={theme.colors.black}
          />
        </IconContainer>
        <Spacer size={20} />
        <StyledText>
          Call us at&nbsp;
          <StyledAnchor href="tel:855-400-8566">(855) 400-8566.</StyledAnchor>
          &nbsp;We are available from 1pm-8pm Eastern on Mondays, 11am-8pm
          Eastern Tuesday through Friday, and 11am-3pm Eastern on Saturday.
        </StyledText>
      </FlexRow>
      <Spacer size={26} />
      <FlexRow>
        <IconContainer>
          <ChatIcon
            width={22}
            height={21}
            fill={theme.colors.black}
          />
        </IconContainer>
        <Spacer size={20} />
        <StyledText>
          Chat with us by&nbsp;
          {/* @FIXME: need to know what is supposed to happen on click here */}
          {/* <Link> */}
          <StyledPseudoAnchor onClick={openChat}>clicking here.</StyledPseudoAnchor>
          {/* </Link> */}
          &nbsp;We are available from 1pm-8pm Eastern on Mondays, 11am-8pm Eastern
          Tuesday through Friday, and 11am-3pm Eastern on Saturday.
        </StyledText>
      </FlexRow>
      <Spacer size={26} />
      <FlexRow>
        <IconContainer>
          <EmailIcon
            width={20}
            height={16}
            fill={theme.colors.black}
          />
        </IconContainer>
        <Spacer size={20} />
        <StyledText>
          Email us at&nbsp;
          <StyledAnchor
            href="mailto:info@homelister.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            info@homelister.com
          </StyledAnchor>
        </StyledText>
      </FlexRow>
      <Spacer size={26} />
      <FlexRow>
        <IconContainer>
          <QuestionMarkIcon
            width={20}
            height={20}
            fill={theme.colors.black}
          />
        </IconContainer>
        <Spacer size={20} />
        <StyledText>
          For frequently asked questions,&nbsp;
          <Link
            href="/faq"
            passHref
          >
            <StyledAnchor>click here</StyledAnchor>
          </Link>
          &nbsp;for answers to our most popular questions.
        </StyledText>
      </FlexRow>
      <Spacer size={50} />
      <FlexRow>
        <ContactForm
          formType="contactUs"
          dismissAfter={60000}
          requirePhone={false}
          submitText="Contact Us"
          notInSidebar
        />
      </FlexRow>
    </MaxWidthContainer>
  )
}

export default ContactUsPage
